import { Fragment, useEffect, useState } from 'react'
import { Dialog, Transition } from '@headlessui/react'
import { XIcon, CheckIcon } from '@heroicons/react/outline'
import { toast } from 'react-toastify'
import { Order } from 'models'
import { ListResult } from 'interfaces/queryOptions'
import { list as listOrders } from '../../services/read'
import { LoadingCircle } from 'components/Loading'
import { mergeOrders } from 'modules/Orders/services'

interface MigrateOrderItemsModalProps {
  isOpen: boolean
  onClose: () => void
  targetOrder: Order
}

export function MigrateOrderItemsModal({
  isOpen,
  onClose,
  targetOrder
}: MigrateOrderItemsModalProps) {
  const [mergeableOrders, setMergeableOrders] = useState<ListResult<Order>>()
  const [isLoading, setIsLoading] = useState(false)
  const [selectedOrderIds, setSelectedOrderIds] = useState<number[]>([])

  const plan = targetOrder.relationships?.plan

  // Calcular capacidade disponível no pedido atual
  const getAvailableCapacity = () => {
    const totalQuantity = targetOrder.quantity || 0

    // Contar itens existentes no pedido atual
    const currentChips =
      targetOrder.relationships?.order_chips?.filter((chip: any) =>
        ['ordered', 'suspended'].includes(chip.attributes?.status)
      ) || []

    const currentDevices =
      targetOrder.relationships?.order_devices?.filter(
        (device: any) => device.attributes?.status === 'ordered'
      ) || []

    const currentItemsCount = currentChips.length + currentDevices.length

    return Math.max(0, totalQuantity - currentItemsCount)
  }

  useEffect(() => {
    if (isOpen) {
      fetchOrders()
    }
  }, [isOpen])

  const fetchOrders = async () => {
    setIsLoading(true)

    if (!plan) return
    try {
      const result = await listOrders({
        attributes: ['id', 'token', 'current_status', 'ordered_at', 'quantity'],
        includes: {
          client: ['id'],
          plan: ['nature', 'currency', 'meta'],
          product: ['*'],
          order_chips: ['status', 'item_id', 'deleted_at'],
          order_devices: ['status', 'item_id', 'deleted_at']
        },
        filters: [
          { key: 'current_status', op: 'eq', value: 'delivered' },
          { key: 'client.id', op: 'eq', value: targetOrder.client_id },
          {
            key: 'product.type',
            op: 'eq',
            value: targetOrder.relationships?.product?.attributes?.type
          },
          { key: 'plan.nature', op: 'eq', value: plan.attributes.nature },
          { key: 'plan.currency', op: 'eq', value: plan.attributes.currency }
        ]
      })

      // Filtrar em memória os pedidos que têm itens válidos
      const filteredOrders = result.data.filter((order) => {
        const orderChips = order.relationships?.order_chips || []
        const orderDevices = order.relationships?.order_devices || []

        // Verificar se tem chips válidos (ordered ou suspended)
        const validChips = orderChips.filter((chip: any) =>
          ['ordered', 'suspended'].includes(chip.attributes?.status)
        )

        // Verificar se tem devices válidos (ordered)
        const validDevices = orderDevices.filter(
          (device: any) => device.attributes?.status === 'ordered'
        )

        // Só incluir o pedido se tiver pelo menos um item válido
        return validChips.length > 0 || validDevices.length > 0
      })

      // Filtrar pedidos de acordo com a meta do plano de referência
      const ordersFilteredByMeta = filterOrdersByMeta(filteredOrders)

      // Limpar os itens inválidos de cada pedido
      const cleanedOrders = ordersFilteredByMeta.map((order) => ({
        ...order,
        relationships: {
          ...order.relationships,
          order_chips:
            order.relationships?.order_chips?.filter((chip: any) =>
              ['ordered', 'suspended'].includes(chip.attributes?.status)
            ) || [],
          order_devices:
            order.relationships?.order_devices?.filter(
              (device: any) => device.attributes?.status === 'ordered'
            ) || []
        }
      }))

      setMergeableOrders({
        count: cleanedOrders.length,
        data: cleanedOrders
      })
    } catch (error: any) {
      toast.error(error.suggestedMessage ?? 'Erro ao carregar pedidos')
    } finally {
      setIsLoading(false)
    }
  }

  // Filtra pedidos compatíveis pelo product_id do pedido alvo
  const filterOrdersByMeta = (orders: any[]): any[] => {
    if (orders.length === 0) return []

    const targetProductId = targetOrder.relationships?.product?.id ?? null
    return orders.filter((order) => {
      const orderProductId = order.relationships?.product?.id ?? null
      return orderProductId === targetProductId
    })
  }

  const handleMigrateItems = async () => {
    if (selectedOrderIds.length === 0) return

    try {
      await mergeOrders(targetOrder.id, selectedOrderIds)

      toast.success('Migração concluída com sucesso!')
      onClose()
    } catch (error: any) {
      console.error('Erro na migração:', error)
      toast.error(error.suggestedMessage ?? 'Erro ao migrar itens')
    }
  }
  return (
    <Transition.Root show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-10" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <div className="relative transform overflow-hidden rounded-lg bg-white px-4 pt-5 pb-4 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-2xl sm:p-6">
                <div className="absolute top-0 right-0 hidden pt-4 pr-4 sm:block">
                  <button
                    type="button"
                    className="rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                    onClick={onClose}
                  >
                    <span className="sr-only">Fechar</span>
                    <XIcon className="h-6 w-6" aria-hidden="true" />
                  </button>
                </div>
                <div className="sm:flex sm:items-start">
                  <div className="mt-3 text-center sm:mt-0 sm:text-left w-full">
                    <Dialog.Title
                      as="h3"
                      className="text-lg font-medium leading-6 text-gray-900"
                    >
                      Migrar itens
                    </Dialog.Title>
                    <div className="mt-4">
                      <p className="text-sm text-gray-500 mb-4">
                        Selecione um pedido para migrar seus itens para o pedido
                        atual
                      </p>

                      {isLoading ? (
                        <div className="flex justify-center py-8">
                          <LoadingCircle />
                        </div>
                      ) : !mergeableOrders?.data.length ? (
                        <div className="text-center py-8">
                          <p className="text-gray-500">
                            {getAvailableCapacity() === 0
                              ? 'O pedido já está na capacidade máxima'
                              : 'Este pedido não tem pedidos migráveis'}
                          </p>
                          {getAvailableCapacity() > 0 && (
                            <p className="text-sm text-gray-400 mt-2">
                              Capacidade disponível: {getAvailableCapacity()}
                            </p>
                          )}
                        </div>
                      ) : (
                        <div className="space-y-3 max-h-96 overflow-y-auto">
                          {mergeableOrders.data.map((order) => {
                            const isSelected = selectedOrderIds.includes(
                              order.id
                            )
                            const chipsCount =
                              order.relationships?.order_chips?.length || 0
                            const devicesCount =
                              order.relationships?.order_devices?.length || 0

                            const handleToggleSelection = () => {
                              if (isSelected) {
                                // Remove da seleção
                                setSelectedOrderIds(
                                  selectedOrderIds.filter(
                                    (id) => id !== order.id
                                  )
                                )
                              } else {
                                // Adiciona à seleção
                                setSelectedOrderIds([
                                  ...selectedOrderIds,
                                  order.id
                                ])
                              }
                            }

                            return (
                              <div
                                key={order.id}
                                className={`relative border-2 rounded-lg p-4 cursor-pointer transition-all duration-200 hover:bg-gray-50 ${
                                  isSelected
                                    ? 'border-green-500 bg-green-50'
                                    : 'border-gray-200'
                                }`}
                                onClick={handleToggleSelection}
                              >
                                <div className="flex justify-between items-center">
                                  <div>
                                    <h4 className="font-medium text-gray-900">
                                      Pedido: {order.attributes.token}
                                    </h4>
                                    <p className="text-sm text-gray-500">
                                      {chipsCount > 0 && devicesCount === 0 && (
                                        <>
                                          {chipsCount} chip
                                          {chipsCount !== 1 ? 's' : ''}{' '}
                                          disponíveis
                                        </>
                                      )}
                                      {chipsCount === 0 && devicesCount > 0 && (
                                        <>
                                          {devicesCount} dispositivo
                                          {devicesCount !== 1 ? 's' : ''}{' '}
                                          disponíveis
                                        </>
                                      )}
                                      {chipsCount > 0 && devicesCount > 0 && (
                                        <>
                                          {chipsCount} chip
                                          {chipsCount !== 1 ? 's' : ''} e{' '}
                                          {devicesCount} dispositivo
                                          {devicesCount !== 1 ? 's' : ''}{' '}
                                          disponíveis
                                        </>
                                      )}
                                    </p>
                                  </div>
                                  {isSelected && (
                                    <div className="flex-shrink-0">
                                      <div className="w-6 h-6 bg-green-500 rounded-full flex items-center justify-center">
                                        <CheckIcon
                                          className="w-4 h-4 text-white"
                                          aria-hidden="true"
                                        />
                                      </div>
                                    </div>
                                  )}
                                </div>
                              </div>
                            )
                          })}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Validação e contagem de itens */}
                {selectedOrderIds.length > 0 && (
                  <div className="mt-4 p-3 rounded-md bg-gray-50">
                    {(() => {
                      const selectedOrders =
                        mergeableOrders?.data.filter((order) =>
                          selectedOrderIds.includes(order.id)
                        ) || []

                      const totalSelectedItems = selectedOrders.reduce(
                        (total, order) => {
                          const chipsCount =
                            order.relationships?.order_chips?.length || 0
                          const devicesCount =
                            order.relationships?.order_devices?.length || 0
                          return total + chipsCount + devicesCount
                        },
                        0
                      )

                      const availableCapacity = getAvailableCapacity()
                      const isValidQuantity =
                        totalSelectedItems <= availableCapacity

                      return (
                        <div className="text-sm">
                          <p className="text-gray-700">
                            <span className="font-medium">
                              Itens selecionados:
                            </span>{' '}
                            {totalSelectedItems}
                          </p>
                          <p className="text-gray-700">
                            <span className="font-medium">
                              Capacidade disponível:
                            </span>{' '}
                            {availableCapacity}
                          </p>
                          {!isValidQuantity && (
                            <p className="text-red-600 mt-1">
                              ⚠️ A quantidade de itens selecionados (
                              {totalSelectedItems}) excede a capacidade
                              disponível do pedido ({availableCapacity})
                            </p>
                          )}
                        </div>
                      )
                    })()}
                  </div>
                )}

                <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse sm:space-x-reverse sm:space-x-3">
                  {/* Botão Migrar */}
                  <button
                    type="button"
                    disabled={(() => {
                      if (selectedOrderIds.length === 0) return true

                      const selectedOrders =
                        mergeableOrders?.data.filter((order) =>
                          selectedOrderIds.includes(order.id)
                        ) || []

                      const totalSelectedItems = selectedOrders.reduce(
                        (total, order) => {
                          const chipsCount =
                            order.relationships?.order_chips?.length || 0
                          const devicesCount =
                            order.relationships?.order_devices?.length || 0
                          return total + chipsCount + devicesCount
                        },
                        0
                      )

                      return totalSelectedItems > getAvailableCapacity()
                    })()}
                    className="inline-flex w-full justify-center rounded-md border border-transparent bg-green-600 px-4 py-2 text-base font-medium text-white shadow-sm hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 disabled:bg-gray-300 disabled:cursor-not-allowed sm:ml-3 sm:w-auto sm:text-sm"
                    onClick={handleMigrateItems}
                  >
                    Migrar
                  </button>

                  {/* Botão Cancelar */}
                  <button
                    type="button"
                    className="mt-3 inline-flex w-full justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-base font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 sm:mt-0 sm:w-auto sm:text-sm"
                    onClick={onClose}
                  >
                    Cancelar
                  </button>
                </div>
              </div>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  )
}
